import { Component, EventEmitter, Input, Output, WritableSignal, signal, inject } from '@angular/core'; // Added inject
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import TurndownService from 'turndown'; // Added TurndownService
import { marked } from 'marked'; // Added marked
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TooltipModule } from 'primeng/tooltip'; // Added TooltipModule
import { ILesson, LESSONS_TOKEN, ContentMetadata } from '../../lesson-mini-components/components/lessons.clases'; // Added LESSONS_TOKEN and ContentMetadata
import { FlagLanguagePipe, LangDescTranslationPipe } from '../../../models/lessons.pipes';
import { InputGroupModule } from 'primeng/inputgroup';
import { LessonUtilsService } from '../../../services/lesson-utils.service'; // Added LessonUtilsService
import { TOAST_ALERTS_TOKEN } from '@dataclouder/ngx-core'; // Added TOAST_ALERTS_TOKEN
import { DividerModule } from 'primeng/divider';

@Component({
  selector: 'dc-lesson-metadata-editor',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    InputTextModule,
    TooltipModule, // Added TooltipModule
    FlagLanguagePipe, // Added Pipe
    LangDescTranslationPipe, // Added Pipe
    InputGroupModule,
    DividerModule,
  ],
  templateUrl: './dc-lesson-metadata-editor.component.html',
  styleUrls: ['./dc-lesson-metadata-editor.component.css'],
})
export class DCLessonMetadataEditorComponent {
  // Use signal for input lesson data
  @Input({ required: true }) lesson: WritableSignal<ILesson | undefined> = signal(undefined); // The lesson data itself
  @Input({ required: true }) isLoadingLesson: WritableSignal<boolean> = signal(false); // Shared loading state

  // Outputs for actions
  @Output() saveRequest = new EventEmitter<void>();
  @Output() importNotionRequest = new EventEmitter<void>();
  @Output() improveNotionRequest = new EventEmitter<void>();
  // Removed generateAIRequest Output as it's handled internally now

  // Output removed as the component now updates the input signal directly.
  // @Output() propertyChange = new EventEmitter<{ propertyPath: string; value: any }>();

  // Injected Services
  #lessonUtilsService = inject(LessonUtilsService);
  #toastService = inject(TOAST_ALERTS_TOKEN);
  #lessonService = inject(LESSONS_TOKEN);
  #turndownService = new TurndownService(); // Instantiate TurndownService

  // Method to handle property changes for ROOT properties (e.g., level)
  onRootPropertyChange(property: keyof ILesson, value: any): void {
    this.lesson.update((current) => {
      if (!current) return undefined;
      // Avoid updating metadata directly here
      if (property === 'metadata') {
        console.warn('Direct metadata updates should use onMetadataPropertyChange');
        return current;
      }
      return { ...current, [property]: value };
    });
    // Emit removed - signal is updated directly above.
    // this.propertyChange.emit({ propertyPath: property, value });
  }

  // Method to handle property changes for METADATA properties
  onMetadataPropertyChange(property: keyof ContentMetadata, value: any): void {
    this.lesson.update((current) => {
      if (!current) return undefined;
      const updatedMetadata = { ...(current.metadata ?? {}), [property]: value };
      return { ...current, metadata: updatedMetadata as ContentMetadata };
    });
  }

  // Method to handle property changes for APP EXTENSION properties
  onAppExtensionPropChange(property: string, value: any): void {
    this.lesson.update((current) => {
      if (!current) return undefined;
      // Ensure appExtension exists, initialize if not
      // Convert value to number specifically for 'level'
      const finalValue = property === 'level' ? Number(value) : value;
      const updatedAppExtension = { ...(current.appExtension ?? {}), [property]: finalValue };
      return { ...current, appExtension: updatedAppExtension };
    });
  }

  // Methods to emit action requests
  emitSaveRequest(): void {
    this.saveRequest.emit();
  }

  emitImportNotionRequest(): void {
    this.importNotionRequest.emit();
  }

  emitImproveNotionRequest(): void {
    this.improveNotionRequest.emit();
  }

  /**
   * Generates lesson content using AI, saving the current state first.
   * Moved from DCLessonEditorComponent.
   */
  public async generateByAI(): Promise<void> {
    const currentLesson = this.lesson(); // Get current value
    if (!currentLesson?.id) {
      this.#toastService.warn({ title: 'Guardar primero', subtitle: 'Guarda la lección antes de usar IA.' });
      return;
    }

    this.isLoadingLesson.set(true);
    try {
      const rawHtmlContent = currentLesson.textCoded || '';
      if (!rawHtmlContent) {
        console.warn('No HTML content found in lesson to process. taking just description');
        this.#toastService.info({ title: 'Contenido lección desde 0', subtitle: 'Solo se usará el prompt' });

        const improvedMarkdown = await this.#lessonUtilsService.improveMDWithAI(this.lesson(), 'Create content from description');
        // Convert and save the generated content
        await this._convertMarkdownToHtmlAndSave(improvedMarkdown); // Use extracted method
      } else {
        // Clean orphaned and Save before Improve
        const lessonToSave = this.#lessonUtilsService.cleanOrphanedComponents(currentLesson);
        const savedLesson = await this.#lessonService.postLesson(lessonToSave);

        if (!savedLesson) {
          this.#toastService.error({ title: 'Error al guardar', subtitle: 'No se pudo guardar antes de generar con IA.' });
          throw new Error('Failed to save before AI generation');
        }
        this.lesson.set(savedLesson);

        // Replace encoded JSON with actual text before Markdown conversion
        const processedHtmlContent = this._extractTextFromEncodedJson(rawHtmlContent);

        // Convert the processed HTML (with text instead of JSON) to Markdown
        const markdownText = this.#turndownService.turndown(processedHtmlContent);

        // Use the updated lesson signal value for AI improvement
        const improvedMarkdown = await this.#lessonUtilsService.improveMDWithAI(this.lesson(), markdownText);

        // Convert and save the improved content
        await this._convertMarkdownToHtmlAndSave(improvedMarkdown); // Use extracted method
      }
    } catch (error: any) {
      console.error('Error during AI generation process in metadata editor:', error);
      // Service handles specific AI error toasts, maybe add a general one here if needed
      // this.#toastService.error({ title: 'Error General', subtitle: 'Ocurrió un problema durante el proceso de IA.' });
    } finally {
      this.isLoadingLesson.set(false); // Stop loading
    }
  }

  /**
   * Replaces encoded JSON blocks (~...~) in HTML with their 'settings.text' value.
   * Moved from DCLessonEditorComponent.
   * @param htmlInput The HTML string containing encoded JSON.
   * @returns The processed HTML string with text replacements.
   */
  private _extractTextFromEncodedJson(htmlInput: string): string {
    const jsonRegex = /~(.+?)~/g; // Global flag to replace all occurrences

    return htmlInput.replace(jsonRegex, (match, jsonCoded) => {
      try {
        // Attempt to decode HTML entities that might be present in the JSON string
        const decodedJson = jsonCoded.replace(/"/g, '"').replace(/&/g, '&').replace(/</g, '<').replace(/>/g, '>');
        const data = JSON.parse(decodedJson);
        // Return the text if available, otherwise keep the original match (or an error placeholder)
        return data?.settings?.text || match;
      } catch (error) {
        console.error('Failed to parse JSON inside HTML:', jsonCoded, error);
        return '<!-- invalid component data -->'; // Placeholder for parsing errors
      }
    });
  }

  /**
   * Converts improved Markdown content to HTML, updates the lesson signal,
   * and saves the lesson.
   * @param improvedMarkdown The Markdown content generated by AI.
   * @throws Error if the markdown is empty/null or if saving fails.
   */
  private async _convertMarkdownToHtmlAndSave(improvedMarkdown: string | null | undefined): Promise<void> {
    if (improvedMarkdown) {
      // Convert the improved Markdown back to HTML before setting it
      const improvedHtml = marked(improvedMarkdown) as string;
      // Update the signal directly
      this.lesson.update((current) => (current ? { ...current, textCoded: improvedHtml } : undefined));
      // Save the AI-generated content
      // Ensure lesson() is not undefined before saving
      const lessonToSave = this.lesson();
      if (lessonToSave) {
        await this.#lessonService.postLesson(lessonToSave);
        this.#toastService.success({ title: 'Contenido generado', subtitle: 'Se generó y guardó el contenido con IA.' });
      } else {
        console.error('Lesson signal is undefined, cannot save.');
        this.#toastService.error({ title: 'Error Interno', subtitle: 'No se pudo guardar la lección.' });
        // Throw an error to be caught by the calling method's try/catch
        throw new Error('Lesson signal is undefined during save operation.');
      }
    } else {
      // Log the error, the toast might be handled by the calling service/context
      console.error('AI generation failed or provided markdown was empty.');
      // Let the caller handle the specific user feedback if needed
      // Throw an error to be caught by the calling method's try/catch
      throw new Error('AI generation failed or the resulting markdown was empty.');
    }
  }

  /**
   * Calls the LessonUtilsService to generate a description using AI
   * and updates the lesson signal if successful.
   */
  async triggerGenerateDescriptionAI(): Promise<void> {
    const currentLesson = this.lesson();
    if (!currentLesson) {
      this.#toastService.warn({ title: 'Lección no cargada', subtitle: 'Espera a que la lección se cargue.' });
      return;
    }

    const generatedDescription = await this.#lessonUtilsService.generateDescriptionWithAI(currentLesson);

    if (generatedDescription) {
      // Use the new method for metadata properties
      this.onMetadataPropertyChange('description', generatedDescription);
    }
  }
}
